// MA-837 — the ONE placement-floor rule: only an EVIDENCE floor is a floor.
//
// The mission pick (useTodaysMission), the Journey stone tap (journeyTap
// resolveStoneTap) and /play/foundation's beforeLoad guard all read their
// floor through evidencePlacementFloor below. MA-775 made the mission honour
// only evidence floors (an age seed proves nothing about the child), but the
// tap and the guard kept calling placementFloor() with no provenance, so a
// foundation skill below an AGE-SEEDED reading module was served by Today yet
// unpickable on the Journey and redirected by the guard. Decision + reason:
// docs/build/specs/MA-837-one-floor-rule.md.
//
// Leaf module: imports only the progress layer and the placement-floor
// utility (no React, no react-query, no content packs), so the pure tap and
// the route guard can use it. The diagnostic rung -> module ids below are the
// ladders' own (pinned equal to them in test:ma837-one-floor-rule); the
// ladders themselves import the content reader, which these callers must not.
import { diagnosticResultRung } from "@/engine/diagnosticResultRung";
import {
  recordedModuleSource,
  hasModuleProvenanceEntry,
  ageSeedModules,
  type AgeSeedModules,
  type ModuleStrand,
  type ModuleProvenance,
} from "@/App/progress";
import { placementFloor, isBelowFloor, legacyOrdinal, guardAllowsBelowFloor, type PlacementFloor } from "./placementFloor";
import { FOUNDATION_SKILL_IDS } from "./skillMeta";

/**
 * P-PATHHOME-BUG-2-ROUND-2 (studentAge=null fix) — resolve the age the
 * age-gate should use on the selection path.
 *
 * progress.studentAge is loaded from localStorage keyed by the LOCAL child id
 * and resolves to `null` (defaultProgress) in several link states — but the
 * child's real age also rides on the registry/kids row the SkillTree already
 * reads. Prefer the persisted progress age; fall back to the registry age so
 * the gate never silently no-ops (null -> ageBandForStudent default "5.5-6.0"
 * -> masks nothing -> age-blind walk hands back chapter 1's lowest "4+1" skill).
 *
 * Pure (no React) so the gate behavior can be pinned in a unit test.
 *
 * MA-80 — this is the ONE age source for every serve/tree/session surface,
 * always called as `resolveSelectionAge(app.progress.studentAge,
 * activeChildAge(app))`: the mission pick below, the four session screens
 * (Math/Reading/Mixed/Foundation: band -> session length, chunk size,
 * auto-read), SkillTree, SkillDetail and useJourneyView (tree/Journey band
 * fit), StoryLibraryScreen, ReadTab, StoryReader, DayCompleteCelebration
 * (reading level / auto-read), onboarding parent-results and the three
 * Parent Settings age reads (band/pacing, level check, auto-read switch).
 * Never
 * read `progress.studentAge` raw (null -> silent "5.5-6.0") or the registry
 * age alone (stale after a cross-device age change, which App.jsx's cloud
 * merge writes to progress only). Pinned per surface, executed, in
 * selectionAgeFallback.test.mjs.
 */
export function resolveSelectionAge(
  progressAge: number | null | undefined,
  registryAge: number | null | undefined,
): number | null {
  if (typeof progressAge === "number" && Number.isFinite(progressAge)) {
    return progressAge;
  }
  if (typeof registryAge === "number" && Number.isFinite(registryAge)) {
    return registryAge;
  }
  return null;
}

/**
 * MA-775 — is this strand's placement floor EVIDENCE (exempt from the age
 * masks and a placement floor for the ranker) rather than an age seed?
 *  1. a provenance entry for the CURRENT module (progress.moduleProvenance,
 *     written by onboarding / the diagnostic / Settings / the level check):
 *     evidence iff its source is not "age";
 *  2. otherwise, the module equals this age's seed (`seeds.own`,
 *     applyPriorBoost(defaultsForAge(age), priors)) → age (the item's
 *     interim rule);
 *  3. the stored onboarding diagnostic (`diagnosticModule`, its terminal
 *     rung's module) chose exactly this module → evidence;
 *  4. NO entry at all (a pre-MA-775 row, or a device that never wrote one)
 *     and the module equals ANY band's seed → age: the legacy Settings
 *     change kept the old age's seed, the midpoint era seeded half a band
 *     high. A STALE entry skips this step — post-MA-775 a module moved by a
 *     writer that records nothing (an engine mastery advance) is evidence;
 *  5. anything else → evidence. Unknown age → evidence (pre-MA-775).
 * `seeds` is plain data from the progress layer (App/progress.ts
 * ageSeedModules, built on the LEAF module engine/ageStartModules.ts) — so
 * neither this module nor the ADR-069 mastery pipelines reach placementEngine
 * or placementValidation, directly or transitively (MA-131 boundary; the
 * reach check is in the MA-775 PR body). Pure; exported for the test.
 */
export function isEvidenceFloor(
  strand: ModuleStrand,
  progress: {
    mathModuleId?: string | null;
    readingModuleId?: string | null;
    moduleProvenance?: ModuleProvenance | null;
  } | null | undefined,
  effectiveAge: number | null | undefined,
  seeds: AgeSeedModules,
  diagnosticModule?: ((strand: ModuleStrand) => string | null | undefined) | null,
): boolean {
  const recorded = recordedModuleSource(progress, strand);
  if (recorded) return recorded !== "age";
  if (typeof effectiveAge !== "number" || !Number.isFinite(effectiveAge) || !seeds.own) return true;
  const current = strand === "math" ? progress?.mathModuleId : progress?.readingModuleId;
  if (!current) return true;
  if (current === (strand === "math" ? seeds.own.mathModuleId : seeds.own.readingModuleId)) return false;
  if (diagnosticModule && diagnosticModule(strand) === current) return true;
  if (!hasModuleProvenanceEntry(progress, strand) && seeds.anyBand[strand].includes(current)) return false;
  return true;
}


/** The terminal-rung -> module id maps of the two onboarding diagnostics
 *  (mathDiagnosticStaircase.MATH_DIAGNOSTIC_STAIRCASE_LADDER and
 *  readingDiagnosticContent.READING_DIAGNOSTIC_LADDER moduleIds, in order). */
export const DIAGNOSTIC_RUNG_MODULE_IDS: Readonly<Record<ModuleStrand, readonly string[]>> = {
  math: ["math.07", "math.09", "math.11", "math.14"],
  reading: ["reading.0a", "reading.0b", "reading.06", "reading.09", "reading.11", "reading.12", "reading.15"],
};

export interface FloorProgressLike {
  placementStatus?: string | null;
  readingModuleId?: string | null;
  mathModuleId?: string | null;
  studentAge?: number | null;
  parentPriors?: Parameters<typeof ageSeedModules>[1];
  moduleProvenance?: ModuleProvenance | null;
  placementResults?: unknown;
  readingPlacementResults?: unknown;
}

/** The module the stored onboarding diagnostic resolved to for a strand (its
 *  placement rung; a pre-MA-1120 row: its terminal rung), or null. Same clamp as the ladders' own *ModuleIdForRung. */
export function storedDiagnosticModule(
  progress: FloorProgressLike | null | undefined,
  strand: ModuleStrand,
): string | null {
  const r = (strand === "math" ? progress?.placementResults : progress?.readingPlacementResults) as
    | { source?: unknown; finalRung?: unknown; placementRung?: unknown; total?: unknown }
    | null
    | undefined;
  // MA-1120 (RED r1 M1): the PLACEMENT rung, else finalRung (pre-MA-1120 rows)
  const rung = diagnosticResultRung(r);
  if (!r || rung === null || !(Number(r.total) > 0)) return null;
  const want = strand === "math" ? "math_diagnostic_staircase" : "reading_diagnostic_staircase";
  if (r.source !== want) return null;
  const ids = DIAGNOSTIC_RUNG_MODULE_IDS[strand];
  return ids[Math.max(0, Math.min(ids.length - 1, rung))];
}

/**
 * The EVIDENCE floor: the strand's placement floor iff it is evidence, else
 * null. It alone EXEMPTS its skill from the age masks, takes the ranker's
 * floor-skill preference (MA-837) and is the Journey's served stone.
 * `registryAge` is the age fallback (resolveSelectionAge).
 */
export function evidencePlacementFloor(
  strand: ModuleStrand,
  progress: FloorProgressLike | null | undefined,
  registryAge?: number | null,
): PlacementFloor | null {
  if (!progress) return null;
  const age = resolveSelectionAge(progress.studentAge, registryAge);
  const seeds = ageSeedModules(age, progress.parentPriors);
  return isEvidenceFloor(strand, progress, age, seeds, (s) => storedDiagnosticModule(progress, s))
    ? placementFloor(strand, progress)
    : null;
}

/**
 * MA-838 — the floor for "what is BELOW it": the evidence floor, else (below-only)
 * the floor of a strand with NO provenance entry at all whose module is a module a
 * diagnostic rung can produce, is a band seed ABOVE this age's own seed, and is not
 * this age's seed. Such a module may be a diagnostic floor whose provenance was
 * lost (fresh device, offline/unlinked onboarding, pre-MA-775 row) or an old age
 * seed a Settings age change kept; they cannot be told apart, so it is honoured
 * ONLY to keep the child out of foundation content below it (the guard, the tap
 * and the mission's below-floor re-rank). A below-only floor (`belowOnly: true`)
 * does NOT exempt its skill from the age masks, does NOT take the floor-skill
 * preference and is NOT the Journey's served stone: an age seed must not act as
 * an evidence floor (MA-775, MA-81 RED F1). One rule for all three consumers.
 */
export function belowFloor(
  strand: ModuleStrand,
  progress: FloorProgressLike | null | undefined,
  registryAge?: number | null,
): PlacementFloor | null {
  const ev = evidencePlacementFloor(strand, progress, registryAge);
  if (ev || !progress) return ev;
  const age = resolveSelectionAge(progress.studentAge, registryAge);
  const seeds = ageSeedModules(age, progress.parentPriors);
  if (!seeds.own || hasModuleProvenanceEntry(progress, strand)) return null;
  const current = strand === "math" ? progress.mathModuleId : progress.readingModuleId;
  if (!current || !seeds.anyBand[strand].includes(current)) return null;
  if (!DIAGNOSTIC_RUNG_MODULE_IDS[strand].includes(current)) return null;
  const own = strand === "math" ? seeds.own.mathModuleId : seeds.own.readingModuleId;
  const a = legacyOrdinal(strand, current);
  const b = legacyOrdinal(strand, own);
  if (!(a >= 0 && b >= 0 && a > b)) return null;
  const f = placementFloor(strand, progress);
  return f ? { ...f, belowOnly: true } : null;
}

/** The mission's floors, ONE function for useTodaysMission and the tests:
 *  `floors` go to the ranker (below-floor re-rank); `exemptSkillIds` are the
 *  evidence floors' skills only (age-mask exemption, Journey served stone). */
export function missionPlacementFloors(
  progress: FloorProgressLike | null | undefined,
  registryAge?: number | null,
) {
  const floors = { reading: belowFloor("reading", progress, registryAge), math: belowFloor("math", progress, registryAge) };
  const exemptSkillIds = [floors.reading, floors.math]
    .filter((f): f is NonNullable<typeof f> => !!f && !f.belowOnly)
    .map((f) => f.skillId);
  return { floors, exemptSkillIds };
}

/**
 * /play/foundation's beforeLoad decision, pure: a foundation skill strictly
 * below the child's floor (belowFloor) is redirected to the floor skill's own
 * surface; null = let the route load. Same floor as the mission's re-rank and
 * the Journey tap. The route calls this; so does the tests.
 */
export function foundationGuardRedirect(
  skill: string,
  progress: FloorProgressLike | null | undefined,
  registryAge?: number | null,
  // MA-851: the demotion valve of the floor skill is lowered (see placementFloor.guardAllowsBelowFloor)
  valveLowered = false,
): { to: "/play/foundation" | "/play/reading"; skillId: string; floor: PlacementFloor } | null {
  const floor = belowFloor("reading", progress, registryAge);
  if (!floor || !isBelowFloor(skill, floor)) return null;
  if (guardAllowsBelowFloor(skill, floor, valveLowered)) return null;
  return {
    to: FOUNDATION_SKILL_IDS.includes(floor.skillId) ? "/play/foundation" : "/play/reading",
    skillId: floor.skillId,
    floor,
  };
}
